// ── Smoke Fog Overlay ──
// When the player camera is inside an active smoke cloud, applies a CSS fog
// overlay that reduces visibility — matching CS:GO interior smoke behavior.
// Uses smooth transitions (lerp over ~200ms) for organic feel.

import * as THREE from 'three';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SmokeFogCloud {
  position: THREE.Vector3;
  radius: number;
  isActive(): boolean;
}

// ---------------------------------------------------------------------------
// Registry — active smoke clouds tracked for fog calculation
// ---------------------------------------------------------------------------

const activeClouds: Set<SmokeFogCloud> = new Set();

export function registerSmokeCloud(cloud: SmokeFogCloud): void {
  activeClouds.add(cloud);
}

export function unregisterSmokeCloud(cloud: SmokeFogCloud): void {
  activeClouds.delete(cloud);
}

export function clearAllSmokeClouds(): void {
  activeClouds.clear();
}

// ---------------------------------------------------------------------------
// Fog intensity calculation
// ---------------------------------------------------------------------------

/** Maximum fog opacity when fully inside smoke center. */
const MAX_FOG_OPACITY = 0.85;

/**
 * Calculate the fog intensity for a given camera position.
 * Returns 0 when outside all smoke clouds, up to MAX_FOG_OPACITY at center.
 * Takes the maximum intensity from all overlapping clouds.
 */
export function getSmokeFogIntensity(cameraPosition: THREE.Vector3): number {
  let maxIntensity = 0;

  for (const cloud of activeClouds) {
    if (!cloud.isActive()) continue;

    const distance = cameraPosition.distanceTo(cloud.position);
    if (distance >= cloud.radius) continue;

    // Linear falloff: 1.0 at center, 0.0 at edge
    const t = 1 - distance / cloud.radius;
    const intensity = t * MAX_FOG_OPACITY;

    if (intensity > maxIntensity) {
      maxIntensity = intensity;
    }
  }

  return maxIntensity;
}

// ---------------------------------------------------------------------------
// DOM overlay — CSS fog effect (similar to flash overlay)
// ---------------------------------------------------------------------------

let overlayEl: HTMLElement | null = null;
let currentOpacity = 0;

/** Lerp speed: fraction of delta per frame at 60fps (~200ms to reach target). */
const LERP_SPEED = 8;

function ensureOverlay(): HTMLElement {
  if (overlayEl) return overlayEl;

  overlayEl = document.createElement('div');
  overlayEl.id = 'smoke-fog-overlay';
  overlayEl.style.cssText =
    'position:fixed;top:0;left:0;width:100vw;height:100vh;' +
    'background:radial-gradient(ellipse at center, rgba(200,195,185,1) 0%, rgba(160,155,145,0.9) 60%, rgba(120,115,105,0.7) 100%);' +
    'pointer-events:none;z-index:190;opacity:0;';
  document.body.appendChild(overlayEl);
  return overlayEl;
}

/**
 * Pre-create the fog overlay DOM element during initialization instead of
 * lazily on first use (avoids layout reflow during gameplay).
 */
export function initSmokeFog(): void {
  ensureOverlay();
}

/**
 * Call once per frame from the render loop.
 * Smoothly transitions the fog overlay opacity based on camera position
 * relative to active smoke clouds.
 */
export function updateSmokeFog(cameraPosition: THREE.Vector3, dt = 1 / 60): void {
  // Prune inactive clouds from the registry
  // Collect first to avoid deleting from Set during iteration
  const toRemove: SmokeFogCloud[] = [];
  for (const cloud of activeClouds) {
    if (!cloud.isActive()) toRemove.push(cloud);
  }
  for (const cloud of toRemove) activeClouds.delete(cloud);

  const targetOpacity = getSmokeFogIntensity(cameraPosition);
  const overlay = ensureOverlay();

  // Smooth lerp toward target
  const lerpFactor = 1 - Math.exp(-LERP_SPEED * dt);
  currentOpacity += (targetOpacity - currentOpacity) * lerpFactor;

  // Snap to 0 when very small to avoid permanent faint overlay
  if (currentOpacity < 0.005) {
    currentOpacity = 0;
  }

  overlay.style.opacity = String(currentOpacity);
}

/**
 * Dispose the fog overlay and reset state.
 */
export function disposeSmokeFog(): void {
  if (overlayEl) {
    overlayEl.remove();
    overlayEl = null;
  }
  currentOpacity = 0;
  activeClouds.clear();
}
